import { useState, useCallback } from "react";

import {
  RegistrationInputValidationMap,
  InlineError,
  TextInputField,
} from "../types/userRegistrationForm";

const useInlineErrorStateManager = () => {
  const [inlineErrorState, setInlineErrorState] = useState<InlineError>({});
  const [timeoutIdMap, setTimeoutIdMap] = useState<{
    [key in TextInputField]?: NodeJS.Timeout;
  }>({});

  const setInlineError = (field: TextInputField, value: string) => {
    setInlineErrorState((prevData) => ({
      ...prevData,
      [field]: value,
    }));
  };

  const updateInlineErrorState = (
    updatedInputValidationMap: Partial<RegistrationInputValidationMap>,
  ) => {
    const updatedInlineError = Object.entries(updatedInputValidationMap).reduce(
      (map, [key, validationObject]) => {
        const inputField = key as TextInputField;
        map[inputField] = validationObject.errorMessage;

        return map;
      },
      {} as InlineError,
    );

    setInlineErrorState((prevData) => ({
      ...prevData,
      ...updatedInlineError,
    }));
  };

  // Consider using lodash debounce instead of setTimeout
  const updateInlineErrorStateWithDelay = (
    inputField: TextInputField,
    inputValidationMap: Partial<RegistrationInputValidationMap>,
  ) => {
    clearTimeout(timeoutIdMap[inputField]);

    const timeoutId = setTimeout(() => {
      updateInlineErrorState(inputValidationMap);
    }, 500);

    setTimeoutIdMap((prevTimeoutIdMap) => ({
      ...prevTimeoutIdMap,
      [inputField]: timeoutId,
    }));
  };

  const clearInlineError = (field: TextInputField) => setInlineError(field, "");

  const clearAllInlineErrors = useCallback(() => setInlineErrorState({}), []);

  return {
    inlineErrorState,
    updateInlineErrorStateWithDelay,
    setInlineErrorState,
    clearInlineError,
    clearAllInlineErrors,
  };
};

export default useInlineErrorStateManager;
